/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import { Card, Row, Col, Button, Dropdown } from 'react-bootstrap'
import { Price } from 'interfaces/price'
import { SandboxInfo } from 'hooks/use-validate-hubspot-deal'

type ProductType = 'blue' | 'one' | 'infra' | 'infra200' | 'reseller'

interface LineItemDisplay {
  name: string
  quantity: number
  price: number
  period: string
  total: number
  priceId: string
  productId: string
  discount?: number // Manual discount applied via dropdown
  isAddon?: boolean
  parentProductType?: ProductType
}

interface ProductSelectionSectionProps {
  lineItems: LineItemDisplay[]
  totalAmount: number
  onProductClick: (type: ProductType) => void
  onClearAll: () => void
  onUpdateDiscount: (
    index: number,
    discountPercentage: number | null,
    discountPriceId?: string
  ) => void
  onEditClick: () => void
  prices?: { [productId: string]: Price[] }
  isLoadingPrices?: boolean
  sandboxInfo?: SandboxInfo | null
  sandboxPhonePreference?: 'same' | 'different' | null
  onSandboxPhonePreferenceChange?: (value: 'same' | 'different') => void
  usePhoneFromSandboxId?: number | null
  sandboxSuggestions?: SandboxInfo[]
}

// Constants for period and product mapping
const PERIOD_TO_METADATA_MAP: { [key: string]: string } = {
  monthly: 'Monthly',
  month: 'Monthly',
  quarterly: 'Quarterly',
  '3 months': 'Quarterly',
  semi_annual: 'Semi-Annual',
  '6 months': 'Semi-Annual',
  yearly: 'Yearly',
  annual: 'Yearly',
  year: 'Yearly',
}

const PhonePreferenceDisplay: React.FC<{
  sandboxPhonePreference: 'same' | 'different'
  usePhoneFromSandboxId?: number | null
  sandboxSuggestions?: SandboxInfo[]
  onSandboxPhonePreferenceChange?: (value: 'same' | 'different') => void
}> = ({
  sandboxPhonePreference,
  usePhoneFromSandboxId,
  sandboxSuggestions = [],
  onSandboxPhonePreferenceChange,
}) => {
  const selectedSibling = usePhoneFromSandboxId
    ? sandboxSuggestions.find((s) => s.id === usePhoneFromSandboxId)
    : null
  const phoneDisplay = selectedSibling
    ? `${selectedSibling.phone_number} (from ${selectedSibling.email})`
    : sandboxPhonePreference === 'same'
    ? 'Keep existing number'
    : 'Request new number'

  return (
    <div
      className="d-flex align-items-center gap-2 mt-3 p-2 rounded"
      style={{ backgroundColor: 'rgba(12, 165, 233, 0.1)' }}
    >
      <ion-icon
        name="call-outline"
        style={{ color: '#0ca5e9', fontSize: '18px' }}
      />
      <span style={{ fontSize: '14px', color: '#a0aec0' }}>Phone:</span>
      <Dropdown>
        <Dropdown.Toggle
          variant="link"
          size="sm"
          style={{
            color: '#0ca5e9',
            textDecoration: 'none',
            padding: '0 4px',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {phoneDisplay}
        </Dropdown.Toggle>
        <Dropdown.Menu className="bg-dark">
          <Dropdown.Item
            className={`text-white ${
              sandboxPhonePreference === 'same' ? 'active' : ''
            }`}
            onClick={() => onSandboxPhonePreferenceChange?.('same')}
          >
            Keep existing number
          </Dropdown.Item>
          <Dropdown.Item
            className={`text-white ${
              sandboxPhonePreference === 'different' ? 'active' : ''
            }`}
            onClick={() => onSandboxPhonePreferenceChange?.('different')}
          >
            Request new number
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  )
}

const ProductSelectionSection: React.FC<ProductSelectionSectionProps> = ({
  lineItems,
  totalAmount,
  onProductClick,
  onClearAll,
  onUpdateDiscount,
  onEditClick,
  prices = {},
  isLoadingPrices = false,
  sandboxInfo,
  sandboxPhonePreference,
  onSandboxPhonePreferenceChange,
  usePhoneFromSandboxId,
  sandboxSuggestions = [],
}) => {
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )

  // Helper function to determine the subscription billing period from line items
  const getSubscriptionBillingPeriod = (): string => {
    const recurringItem = lineItems.find((item) => item.period !== 'one-time')
    return recurringItem?.period || 'monthly'
  }

  // Helper function to convert period string to number of months
  const getPeriodInMonths = (period: string): number => {
    const normalized = period.toLowerCase().replace(/\s+/g, '')
    switch (normalized) {
      case 'month':
      case 'monthly':
        return 1
      case '3months':
      case 'quarterly':
        return 3
      case '6months':
      case 'semiannual':
        return 6
      case 'year':
      case 'yearly':
      case 'annual':
        return 12
      default:
        return 1
    }
  }

  // Helper function to find the discount price ID for a given percentage and product/period
  const getDiscountPriceIdForPercentage = (
    productId: string,
    period: string,
    percentage: number
  ): string | undefined => {
    const productPrices = prices[productId] || []

    // For one-time products, use subscription billing period to match discount prices
    const actualPeriod =
      period === 'one-time' ? getSubscriptionBillingPeriod() : period
    const expectedMonths = getPeriodInMonths(actualPeriod)

    const matchingPrice = productPrices.find((price) => {
      const isDiscount =
        price.metadata?.is_discount === true ||
        price.metadata?.is_discount === 'true'
      if (!isDiscount) return false

      const discountPercentage = parseFloat(
        price.metadata?.discount_percentage || '0'
      )
      if (discountPercentage !== percentage) return false

      // Handle one-time prices (no recurring field)
      if (!price.recurring) {
        // If this is a universal discount (applies_to_all_periods), only show for one-time products
        const hasAppliestoAllPeriods =
          price.metadata?.applies_to_all_periods === 'true' ||
          price.metadata?.applies_to_all_periods === true
        if (hasAppliestoAllPeriods) {
          return period === 'one-time'
        }

        // Otherwise, match by billing_period_months for subscription periods
        const billingPeriodMonths = parseInt(
          price.metadata?.billing_period_months || '0',
          10
        )
        return billingPeriodMonths === expectedMonths
      }

      // For recurring prices, match by billing_period_months metadata if available
      const recurringBillingPeriodMonths = parseInt(
        price.metadata?.billing_period_months || '0',
        10
      )
      if (recurringBillingPeriodMonths > 0) {
        return recurringBillingPeriodMonths === expectedMonths
      }

      // Fallback: match by interval with normalization
      const priceInterval = price.recurring.interval
      const priceIntervalCount = price.recurring.interval_count || 1

      const priceIntervalKey =
        priceIntervalCount === 1
          ? priceInterval
          : `${priceIntervalCount}${priceInterval}s`

      // Normalize both strings for comparison (remove spaces and convert to lowercase)
      const normalizedPriceIntervalKey = priceIntervalKey
        .toLowerCase()
        .replace(/\s+/g, '')
      const normalizedActualPeriod = actualPeriod
        .toLowerCase()
        .replace(/\s+/g, '')

      return normalizedPriceIntervalKey === normalizedActualPeriod
    })

    return matchingPrice?.id
  }

  // Helper function to get discount percentages from price metadata for a specific product/period
  const getDiscountPercentagesForProduct = (
    productId: string,
    period: string
  ): number[] => {
    const productPrices = prices[productId] || []

    // For one-time products, use the subscription billing period to find matching discount prices
    const actualPeriod =
      period === 'one-time' ? getSubscriptionBillingPeriod() : period
    const expectedMonths = getPeriodInMonths(actualPeriod)

    // Filter for discount prices matching the period
    const discountPrices = productPrices.filter((price) => {
      const isDiscount =
        price.metadata?.is_discount === true ||
        price.metadata?.is_discount === 'true'
      if (!isDiscount) return false

      // Handle one-time prices
      if (!price.recurring) {
        // If this is a universal discount (applies_to_all_periods), only show for one-time products
        const hasAppliestoAllPeriods =
          price.metadata?.applies_to_all_periods === 'true' ||
          price.metadata?.applies_to_all_periods === true
        if (hasAppliestoAllPeriods) {
          return period === 'one-time'
        }

        // Otherwise, match by billing_period_months for subscription periods
        const billingPeriodMonths = parseInt(
          price.metadata?.billing_period_months || '0',
          10
        )
        return billingPeriodMonths === expectedMonths
      }

      // For recurring prices, match by billing_period_months metadata if available
      const recurringBillingPeriodMonths = parseInt(
        price.metadata?.billing_period_months || '0',
        10
      )
      if (recurringBillingPeriodMonths > 0) {
        return recurringBillingPeriodMonths === expectedMonths
      }

      // Fallback: match by interval with normalization
      const priceInterval = price.recurring.interval
      const priceIntervalCount = price.recurring.interval_count || 1
      const priceIntervalKey =
        priceIntervalCount === 1
          ? priceInterval
          : `${priceIntervalCount}${priceInterval}s`

      // Normalize both strings for comparison (remove spaces and convert to lowercase)
      const normalizedPriceIntervalKey = priceIntervalKey
        .toLowerCase()
        .replace(/\s+/g, '')
      const normalizedActualPeriod = actualPeriod
        .toLowerCase()
        .replace(/\s+/g, '')

      return normalizedPriceIntervalKey === normalizedActualPeriod
    })

    // Extract discount percentages from metadata and sort
    const percentages = discountPrices
      .map((price) => parseFloat(price.metadata?.discount_percentage || '0.00'))
      .filter((percentage) => percentage > 0)
      .sort((a, b) => a - b)

    // Remove duplicates
    return [...new Set(percentages)]
  }

  const getProductColor = (productType?: ProductType): string => {
    switch (productType) {
      case 'blue':
        return '#2563eb'
      case 'infra':
        return '#0ca5e9'
      case 'infra200':
        return '#0891b2'
      case 'one':
        return '#10b981'
      default:
        return '#2563eb'
    }
  }

  // Find the first base product type in the cart (excluding add-ons)
  const selectedBaseProductType = lineItems.find(
    (item) => !item.isAddon
  )?.parentProductType

  const products = [
    {
      type: 'blue' as ProductType,
      label: 'Sales Enablement',
      backgroundColor: '#2563eb',
      shadowColor: 'rgba(37, 99, 235, 0.3)',
    },
    {
      type: 'infra' as ProductType,
      label: 'Infrastructure',
      backgroundColor: '#0ca5e9',
      shadowColor: 'rgba(12, 165, 233, 0.3)',
    },
    {
      type: 'infra200' as ProductType,
      label: 'Infras 200 Contacts',
      backgroundColor: '#0891b2',
      shadowColor: 'rgba(8, 145, 178, 0.3)',
    },
    {
      type: 'one' as ProductType,
      label: 'One',
      backgroundColor: '#10b981',
      shadowColor: 'rgba(16, 185, 129, 0.3)',
    },
    {
      type: 'reseller' as ProductType,
      label: 'Reseller',
      backgroundColor: '#9333ea',
      shadowColor: 'rgba(147, 51, 234, 0.3)',
    },
  ]

  return (
    <>
      {/* Products Section */}
      <Card className="mb-4 text-white bg-dark">
        <Card.Body>
          <h2
            style={{ fontSize: '18px', fontWeight: 600, marginBottom: '1rem' }}
          >
            Products
          </h2>
          <Row>
            {products.map((product) => {
              const isDisabled = (() => {
                // Sandbox: disable "One" entirely
                if (sandboxInfo && product.type === 'one') return true
                // If a base product is in cart, disable other types
                if (
                  selectedBaseProductType &&
                  selectedBaseProductType !== product.type
                ) {
                  return true
                }
                return false
              })()
              return (
                <Col key={product.type} md className="mb-3 mb-md-0">
                  <Button
                    onClick={() => !isDisabled && onProductClick(product.type)}
                    disabled={isDisabled}
                    style={{
                      backgroundColor: product.backgroundColor,
                      border: 'none',
                      borderLeft: `4px solid ${product.backgroundColor}`,
                      borderRadius: '8px',
                      padding: '12px',
                      width: '100%',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      opacity: isDisabled ? 0.4 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.transform =
                          'translateY(-2px) scale(1.02)'
                        e.currentTarget.style.boxShadow = `0 4px 12px ${product.shadowColor}`
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isDisabled) {
                        e.currentTarget.style.transform =
                          'translateY(0) scale(1)'
                        e.currentTarget.style.boxShadow =
                          '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    {product.label}
                  </Button>
                </Col>
              )
            })}
          </Row>
        </Card.Body>
      </Card>

      {/* PO Summary Section */}
      <Card className="mb-4 text-white bg-dark">
        <Card.Body style={{ position: 'relative', overflow: 'visible' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
              PO Summary
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="outline-light"
                size="sm"
                onClick={onEditClick}
                disabled={lineItems.length === 0}
                style={{
                  border: '1px solid #4a5568',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  opacity: lineItems.length === 0 ? 0.4 : 1,
                  cursor: lineItems.length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                <ion-icon name="create-outline" />
              </Button>
              <Button
                variant="outline-light"
                size="sm"
                onClick={onClearAll}
                style={{
                  border: '1px solid #4a5568',
                  borderRadius: '6px',
                  padding: '6px 12px',
                }}
              >
                <ion-icon name="trash-outline" />
              </Button>
            </div>
          </div>

          {lineItems.length === 0 ? (
            <div className="text-center py-5">
              <div
                style={{ fontSize: '48px', marginBottom: '1rem', opacity: 0.5 }}
              >
                <ion-icon name="document-text-outline" />
              </div>
              <div>
                No items added yet. Click a product above to get started.
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  overflowX: openDropdownIndex !== null ? 'visible' : 'auto',
                  overflowY: 'visible',
                  position: 'static',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    position: 'relative',
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: '2px solid #4a5568' }}>
                      <th
                        style={{
                          color: '#a0aec0',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '12px 16px',
                          textAlign: 'left',
                          width: '35%',
                        }}
                      >
                        Product
                      </th>
                      <th
                        style={{
                          color: '#a0aec0',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '12px 16px',
                          textAlign: 'right',
                          width: '12%',
                        }}
                      >
                        Quantity
                      </th>
                      <th
                        style={{
                          color: '#a0aec0',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '12px 16px',
                          textAlign: 'right',
                          width: '12%',
                        }}
                      >
                        Price
                      </th>
                      <th
                        style={{
                          color: '#a0aec0',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '12px 16px',
                          textAlign: 'right',
                          width: '12%',
                        }}
                      >
                        Discount
                      </th>
                      <th
                        style={{
                          color: '#a0aec0',
                          fontSize: '13px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '12px 16px',
                          textAlign: 'right',
                          width: '15%',
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr
                        key={index}
                        style={{
                          position: 'relative',
                          zIndex: openDropdownIndex === index ? 1000 : 'auto',
                        }}
                      >
                        <td
                          colSpan={5}
                          style={{ padding: '8px 0', position: 'relative' }}
                        >
                          <div
                            style={{
                              backgroundColor: '#2d3748',
                              padding: '16px',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${getProductColor(
                                item.parentProductType
                              )}`,
                              display: 'flex',
                              alignItems: 'center',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              cursor: 'default',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#374151'
                              e.currentTarget.style.transform =
                                'translateX(4px)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#2d3748'
                              e.currentTarget.style.transform = 'translateX(0)'
                            }}
                          >
                            <div style={{ flex: 2, position: 'relative' }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                }}
                              >
                                {item.name}
                                <Dropdown
                                  onToggle={(isOpen) => {
                                    // Only allow opening if discounts are available
                                    if (
                                      getDiscountPercentagesForProduct(
                                        item.productId,
                                        item.period
                                      ).length > 0
                                    ) {
                                      setOpenDropdownIndex(
                                        isOpen ? index : null
                                      )
                                    }
                                  }}
                                  show={openDropdownIndex === index}
                                >
                                  <Dropdown.Toggle
                                    as="span"
                                    bsPrefix="custom-dropdown-toggle"
                                    style={{
                                      cursor:
                                        getDiscountPercentagesForProduct(
                                          item.productId,
                                          item.period
                                        ).length > 0
                                          ? 'pointer'
                                          : 'not-allowed',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      color: item.discount
                                        ? getProductColor(
                                            item.parentProductType
                                          )
                                        : '#ffffff',
                                      opacity:
                                        getDiscountPercentagesForProduct(
                                          item.productId,
                                          item.period
                                        ).length > 0
                                          ? 1
                                          : 0.4,
                                    }}
                                  >
                                    <ion-icon name="pricetag-outline" />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu
                                    className="discount-dropdown-menu"
                                    renderOnMount
                                    popperConfig={{
                                      strategy: 'fixed',
                                      modifiers: [
                                        {
                                          name: 'flip',
                                          enabled: true,
                                        },
                                        {
                                          name: 'offset',
                                          options: {
                                            offset: [0, 4],
                                          },
                                        },
                                        {
                                          name: 'preventOverflow',
                                          options: {
                                            boundary: 'viewport',
                                          },
                                        },
                                      ],
                                    }}
                                  >
                                    <div className="discount-dropdown-header">
                                      Add Discount
                                    </div>
                                    <div className="discount-dropdown-scroll-container">
                                      {isLoadingPrices ? (
                                        <div
                                          style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            color: '#a0aec0',
                                            fontSize: '13px',
                                          }}
                                        >
                                          Loading...
                                        </div>
                                      ) : (
                                        <>
                                          <Dropdown.Item
                                            className={`discount-dropdown-item ${
                                              item.discount === 0 ||
                                              !item.discount
                                                ? 'active'
                                                : ''
                                            }`}
                                            onClick={() =>
                                              onUpdateDiscount(index, null)
                                            }
                                          >
                                            No Discount
                                          </Dropdown.Item>
                                          {getDiscountPercentagesForProduct(
                                            item.productId,
                                            item.period
                                          ).map((percentage) => {
                                            const discountPriceId =
                                              getDiscountPriceIdForPercentage(
                                                item.productId,
                                                item.period,
                                                percentage
                                              )
                                            return (
                                              <Dropdown.Item
                                                key={percentage}
                                                className={`discount-dropdown-item ${
                                                  item.discount === percentage
                                                    ? 'active'
                                                    : ''
                                                }`}
                                                onClick={() =>
                                                  onUpdateDiscount(
                                                    index,
                                                    percentage,
                                                    discountPriceId
                                                  )
                                                }
                                              >
                                                {percentage}%
                                              </Dropdown.Item>
                                            )
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#a0aec0',
                                  marginTop: '2px',
                                }}
                              >
                                ${item.price.toFixed(2)} / {item.period}
                              </div>
                            </div>
                            <div style={{ flex: 0.75, textAlign: 'right' }}>
                              {item.quantity}
                            </div>
                            <div style={{ flex: 0.75, textAlign: 'right' }}>
                              ${item.price.toFixed(2)}
                            </div>
                            <div
                              style={{
                                flex: 0.75,
                                textAlign: 'right',
                                color: item.discount ? '#10b981' : '#ffffff',
                                fontWeight: item.discount ? 600 : 400,
                              }}
                            >
                              {item.discount ? `${item.discount}%` : '-'}
                            </div>
                            <div
                              style={{
                                flex: 1,
                                textAlign: 'right',
                              }}
                            >
                              $
                              {(
                                item.total *
                                (1 - (item.discount || 0) / 100)
                              ).toFixed(2)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Phone Preference (Sandbox) */}
              {sandboxPhonePreference && (
                <PhonePreferenceDisplay
                  sandboxPhonePreference={sandboxPhonePreference}
                  usePhoneFromSandboxId={usePhoneFromSandboxId}
                  sandboxSuggestions={sandboxSuggestions}
                  onSandboxPhonePreferenceChange={
                    onSandboxPhonePreferenceChange
                  }
                />
              )}

              <div
                style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '2px solid #4a5568',
                }}
              >
                {/* Subtotal */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                  }}
                >
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginRight: '2rem',
                    }}
                  >
                    Subtotal:
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 600 }}>
                    $
                    {lineItems
                      .reduce((sum, item) => sum + item.total, 0)
                      .toFixed(2)}
                  </span>
                </div>

                {/* Discounts - Show each discount separately like Stripe */}
                {lineItems
                  .filter((item) => item.discount && item.discount > 0)
                  .map((item, index) => {
                    const discountAmount = item.total * (item.discount! / 100)
                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            marginRight: '2rem',
                          }}
                        >
                          {item.name} ({item.discount}% off):
                        </span>
                        <span
                          style={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#10b981',
                          }}
                        >
                          -${discountAmount.toFixed(2)}
                        </span>
                      </div>
                    )
                  })}

                {/* Total */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #4a5568',
                  }}
                >
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      marginRight: '2rem',
                    }}
                  >
                    Total:
                  </span>
                  <span style={{ fontSize: '24px', fontWeight: 700 }}>
                    ${totalAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  )
}

export default ProductSelectionSection
